import React, { useState } from 'react';
import { Navigation } from '../components/Navigation';
import { Newsletter } from '../components/Newsletter';
import { StudentLoans } from '../components/StudentLoans';
import { motion } from 'framer-motion';
import { GraduationCap, Book, Calculator, DollarSign, Calendar, Briefcase, Building2, PiggyBank, ArrowDownToLine, CheckCircle, XCircle, Users } from 'lucide-react';
import { FinancialChart } from '../components/Chart';
import { format, addMonths, differenceInMonths } from 'date-fns';

interface RepaymentStrategy {
  name: string;
  description: string;
  pros: string[];
  cons: string[];
  bestFor: string[];
}

const StudentLoanCalculator = () => {
  const [monthlyPayment, setMonthlyPayment] = useState<number>(0);
  const [showRepaymentStrategies, setShowRepaymentStrategies] = useState(false);
  const [selectedStrategy, setSelectedStrategy] = useState<string | null>(null);
  const [showCalculator, setShowCalculator] = useState(true);

  const repaymentStrategies: RepaymentStrategy[] = [
    {
      name: "Standard Repayment",
      description: "Fixed monthly payments over 10 years",
      pros: [
        "Predictable monthly payments",
        "Pay less interest over time",
        "Debt-free in 10 years"
      ],
      cons: [
        "Higher monthly payments",
        "Less flexibility",
        "May strain early-career budgets"
      ],
      bestFor: [
        "Borrowers with stable income",
        "Those who can afford higher payments",
        "People wanting to minimize interest"
      ]
    },
    {
      name: "Income-Driven Repayment",
      description: "Payments based on your discretionary income",
      pros: [
        "Lower monthly payments",
        "Payments adjust with income",
        "Potential loan forgiveness"
      ],
      cons: [
        "Pay more interest over time",
        "Longer repayment period",
        "May need to recertify annually"
      ],
      bestFor: [
        "Lower-income borrowers",
        "Public service workers",
        "Those with high debt-to-income ratios"
      ]
    },
    {
      name: "Graduated Repayment",
      description: "Payments start low and increase every two years",
      pros: [
        "Lower initial payments",
        "Good for expected income growth",
        "10-year repayment term"
      ],
      cons: [
        "Pay more interest than standard plan",
        "Payments may become high",
        "Requires income growth"
      ],
      bestFor: [
        "Recent graduates",
        "Those expecting salary increases",
        "People in growing career fields"
      ]
    },
    {
      name: "Avalanche Method",
      description: "Pay highest interest loans first while making minimum payments on others",
      pros: [
        "Minimize total interest paid",
        "Mathematically optimal",
        "Faster debt payoff"
      ],
      cons: [
        "May take time to see progress",
        "Requires discipline",
        "Less psychological reward"
      ],
      bestFor: [
        "Math-oriented people",
        "Those focused on minimizing costs",
        "Borrowers with high-interest loans"
      ]
    }
  ];

  const handleMonthlyPaymentUpdate = (amount: number) => {
    setMonthlyPayment(amount);
  };

  return (
    <div className="min-h-screen bg-[var(--background)] p-6">
      <div className="max-w-7xl mx-auto">
        <Navigation />

        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="p-3 rounded-xl bg-emerald-500/10">
              <GraduationCap className="w-10 h-10 text-emerald-500" />
            </div>
            <h1 className="text-4xl font-bold">Student Loan Calculator</h1>
          </div>
          <p className="text-slate-400 text-lg">
            Plan your student loan repayment strategy and understand your options
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8 mb-8">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="glass-card p-6 border border-emerald-500/20"
          >
            <div className="flex items-center gap-3 mb-4">
              <Book className="w-6 h-6 text-emerald-500" />
              <h3 className="text-lg font-semibold">Education Resources</h3>
            </div>
            <ul className="space-y-3">
              <li className="flex items-center gap-2 text-slate-300">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                Federal vs Private Loans
              </li>
              <li className="flex items-center gap-2 text-slate-300">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                Interest Rate Types
              </li>
              <li className="flex items-center gap-2 text-slate-300">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                Repayment Options
              </li>
              <li className="flex items-center gap-2 text-slate-300">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                Loan Forgiveness Programs
              </li>
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="glass-card p-6 border border-emerald-500/20"
          >
            <div className="flex items-center gap-3 mb-4">
              <Calculator className="w-6 h-6 text-emerald-500" />
              <h3 className="text-lg font-semibold">Payment Summary</h3>
            </div>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-slate-400 mb-1">Monthly Payment</p>
                <p className="text-2xl font-bold text-emerald-500">
                  ${monthlyPayment.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-400 mb-1">Annual Payment</p>
                <p className="text-2xl font-bold text-emerald-500">
                  ${(monthlyPayment * 12).toLocaleString()}
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="glass-card p-6 border border-emerald-500/20"
          >
            <div className="flex items-center gap-3 mb-4">
              <PiggyBank className="w-6 h-6 text-emerald-500" />
              <h3 className="text-lg font-semibold">Quick Tips</h3>
            </div>
            <ul className="space-y-3 text-sm text-slate-300">
              <li className="flex items-start gap-2">
                <DollarSign className="w-4 h-4 text-emerald-500 mt-1 flex-shrink-0" />
                Make extra payments when possible to reduce total interest
              </li>
              <li className="flex items-start gap-2">
                <Calendar className="w-4 h-4 text-emerald-500 mt-1 flex-shrink-0" />
                Set up auto-pay for a potential interest rate reduction
              </li>
              <li className="flex items-start gap-2">
                <Briefcase className="w-4 h-4 text-emerald-500 mt-1 flex-shrink-0" />
                Check if your employer offers loan repayment benefits
              </li>
              <li className="flex items-start gap-2">
                <Building2 className="w-4 h-4 text-emerald-500 mt-1 flex-shrink-0" />
                Consider refinancing if you qualify for better rates
              </li>
            </ul>
          </motion.div>
        </div>

        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-semibold">Loan Calculator</h2>
            <div className="flex gap-4">
              <button
                onClick={() => setShowCalculator(true)}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  showCalculator
                    ? 'bg-emerald-500 text-white'
                    : 'bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500/20'
                }`}
              >
                Calculator
              </button>
              <button
                onClick={() => setShowCalculator(false)}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  !showCalculator
                    ? 'bg-emerald-500 text-white'
                    : 'bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500/20'
                }`}
              >
                Repayment Strategies
              </button>
            </div>
          </div>

          {showCalculator ? (
            <StudentLoans onUpdateMonthlyPayment={handleMonthlyPaymentUpdate} />
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              {repaymentStrategies.map((strategy) => (
                <motion.div
                  key={strategy.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`glass-card p-6 border transition-colors cursor-pointer ${
                    selectedStrategy === strategy.name
                      ? 'border-emerald-500'
                      : 'border-emerald-500/20 hover:border-emerald-500/50'
                  }`}
                  onClick={() => setSelectedStrategy(strategy.name)}
                >
                  <h3 className="text-xl font-semibold mb-4">{strategy.name}</h3>
                  <p className="text-slate-400 mb-4">{strategy.description}</p>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-medium text-emerald-500 mb-2">Pros</h4>
                      <ul className="space-y-2">
                        {strategy.pros.map((pro, index) => (
                          <li key={index} className="flex items-start gap-2 text-sm">
                            <CheckCircle className="w-4 h-4 text-emerald-500 mt-1" />
                            {pro}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-medium text-red-500 mb-2">Cons</h4>
                      <ul className="space-y-2">
                        {strategy.cons.map((con, index) => (
                          <li key={index} className="flex items-start gap-2 text-sm">
                            <XCircle className="w-4 h-4 text-red-500 mt-1" />
                            {con}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="mt-4">
                    <h4 className="font-medium text-emerald-500 mb-2">Best For</h4>
                    <ul className="space-y-2">
                      {strategy.bestFor.map((item, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm">
                          <Users className="w-4 h-4 text-emerald-500 mt-1" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        <div className="grid lg:grid-cols-2 gap-8 mb-8">
          <div className="glass-card p-6 border border-emerald-500/20">
            <h3 className="text-xl font-semibold mb-6">Loan Forgiveness Programs</h3>
            <div className="space-y-4">
              <div className="p-4 bg-emerald-500/5 rounded-lg">
                <h4 className="font-medium mb-2">Public Service Loan Forgiveness (PSLF)</h4>
                <p className="text-sm text-slate-400">
                  Forgives remaining federal loan balance after 120 qualifying payments while working for eligible employers
                </p>
              </div>
              <div className="p-4 bg-emerald-500/5 rounded-lg">
                <h4 className="font-medium mb-2">Teacher Loan Forgiveness</h4>
                <p className="text-sm text-slate-400">
                  Up to $17,500 in forgiveness for eligible teachers working in low-income schools
                </p>
              </div>
              <div className="p-4 bg-emerald-500/5 rounded-lg">
                <h4 className="font-medium mb-2">Income-Driven Forgiveness</h4>
                <p className="text-sm text-slate-400">
                  Forgives remaining balance after 20-25 years of qualifying payments under income-driven plans
                </p>
              </div>
            </div>
          </div>

          <div className="glass-card p-6 border border-emerald-500/20">
            <h3 className="text-xl font-semibold mb-6">Additional Resources</h3>
            <div className="space-y-4">
              <a
                href="https://studentaid.gov"
                target="_blank"
                rel="noopener noreferrer"
                className="block p-4 bg-emerald-500/5 rounded-lg hover:bg-emerald-500/10 transition-colors"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Building2 className="w-5 h-5 text-emerald-500" />
                  <h4 className="font-medium">Federal Student Aid</h4>
                </div>
                <p className="text-sm text-slate-400">
                  Official source for federal student aid information and resources
                </p>
              </a>
              <a
                href="https://www.consumerfinance.gov/paying-for-college/"
                target="_blank"
                rel="noopener noreferrer"
                className="block p-4 bg-emerald-500/5 rounded-lg hover:bg-emerald-500/10 transition-colors"
              >
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="w-5 h-5 text-emerald-500" />
                  <h4 className="font-medium">Consumer Financial Protection Bureau</h4>
                </div>
                <p className="text-sm text-slate-400">
                  Tools and resources for making informed decisions about paying for college
                </p>
              </a>
              <a
                href="https://www.irs.gov/credits-deductions/individuals/student-loan-interest-deduction"
                target="_blank"
                rel="noopener noreferrer"
                className="block p-4 bg-emerald-500/5 rounded-lg hover:bg-emerald-500/10 transition-colors"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Calculator className="w-5 h-5 text-emerald-500" />
                  <h4 className="font-medium">Student Loan Interest Deduction</h4>
                </div>
                <p className="text-sm text-slate-400">
                  Information about tax deductions for student loan interest payments
                </p>
              </a>
            </div>
          </div>
        </div>

        <div className="mt-8">
          <Newsletter 
            title="Stay Informed"
            subtitle="Get updates on student loan policies, forgiveness programs, and repayment strategies."
          />
        </div>
      </div>
    </div>
  );
};

export default StudentLoanCalculator;